/**
 * The roles the API actually issues, mirrored from `utils/choices.py`
 * (ROLE_CHOICES) in assets-api. Import these instead of writing role strings
 * inline.
 *
 * The app had drifted: some screens checked `['MANAGER', 'BRANCH_MANAGER']` and
 * others `['MANAGER', 'ASST_MANAGER']`. BRANCH_MANAGER is not a role the backend
 * can ever return, so those checks quietly resolved to "MANAGER only" and
 * assistant managers saw a different app depending on which screen they opened.
 */
export const ROLES = {
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
  ASST_MANAGER: 'ASST_MANAGER',
  REGIONAL_MANAGER: 'REGIONAL_MANAGER',
  AUDITOR: 'AUDITOR',
  DIRECTOR: 'DIRECTOR',
  DATA_ENTRY: 'DATA_ENTRY',
  SERVICE_PROVIDER: 'SERVICE_PROVIDER',
  LOSS_CONTROL: 'LOSS_CONTROL',
};

/** Matches ADMIN_ROLES in maintenance/permissions.py. */
export const ADMIN_ROLES = [ROLES.ADMIN, ROLES.DIRECTOR];

/** Matches MANAGER_ROLES in maintenance/permissions.py; branch-scoped. */
export const MANAGER_ROLES = [ROLES.MANAGER, ROLES.ASST_MANAGER, ROLES.REGIONAL_MANAGER];

// ---------------------------------------------------------------------------
// Capabilities — what a role may do, so screens ask the question they mean.
// These mirror the server-side rules; the server remains the authority.
// ---------------------------------------------------------------------------

/** Raise a maintenance request. Mirrors permissions.can_create_record. */
export const CAN_RAISE_MAINTENANCE = [...ADMIN_ROLES, ...MANAGER_ROLES];

/**
 * Raise a repair from an asset's own page. Narrower than
 * CAN_RAISE_MAINTENANCE by product decision: only the branch manager and their
 * assistant open repairs from the asset detail screen, even though the server
 * would accept one from an admin or regional manager.
 */
export const CAN_RAISE_ASSET_REPAIR = [ROLES.MANAGER, ROLES.ASST_MANAGER];

/** Approve or decline a maintenance request. Mirrors permissions.can_admin_review. */
export const CAN_APPROVE_MAINTENANCE = ADMIN_ROLES;

/** Sign off an asset return. Mirrors permissions.can_review_return. */
export const CAN_REVIEW_RETURN = [...ADMIN_ROLES, ...MANAGER_ROLES];

/** Edit or delete an asset record. */
export const CAN_EDIT_ASSET = ADMIN_ROLES;

/** Dispose of an asset. */
export const CAN_DISPOSE_ASSET = [...ADMIN_ROLES, ...MANAGER_ROLES];

/** Open, delete or reconcile a stock take. Mirrors audits.permissions.can_manage_audits. */
export const CAN_MANAGE_AUDITS = [...ADMIN_ROLES, ROLES.AUDITOR];

/** See the audit screens at all. Mirrors audits.permissions.can_view_audits. */
export const CAN_VIEW_AUDITS = [...ADMIN_ROLES, ROLES.AUDITOR, ...MANAGER_ROLES];
